import React, { useState } from "react";
import { useData } from "../../context/DataContext";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import { MultiImageUploadField } from "../../utils/ImageUploadField";
import { successToast, errorToast, confirmDelete } from "../../utils/dialog";

export default function AdminBlogPost() {
  const { blogs, addBlog, updateBlog, deleteBlog } = useData();

  const [formData, setFormData] = useState({
    id: null,
    title: "",
    author: "",
    date: new Date().toISOString().split("T")[0],
    excerpt: "",
    tags: [],
    content: [""],
    images: [],
  });

  const [files, setFiles] = useState([]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (index, value) => {
    setFormData((prev) => {
      const updatedContent = [...prev.content];
      updatedContent[index] = value;
      return { ...prev, content: updatedContent };
    });
  };

  const handleAddContentSection = () => {
    setFormData((prev) => ({
      ...prev,
      content: [...prev.content, ""],
    }));
  };

  const handleRemoveContentSection = (index) => {
    setFormData((prev) => {
      const updatedContent = [...prev.content];
      updatedContent.splice(index, 1);
      return { ...prev, content: updatedContent };
    });
  };

  const handleSubmit = async () => {
    if (!formData.title || (formData.id === null && files.length === 0)) {
      errorToast("Title and at least one image are required.");
      return;
    }

    if (formData.id) {
      await updateBlog(formData.id, formData, files);
      successToast("Blog updated.");
    } else {
      await addBlog(formData, files);
      successToast("Blog posted.");
    }

    resetForm();
  };

  const resetForm = () => {
    setFormData({
      id: null,
      title: "",
      author: "",
      date: new Date().toISOString().split("T")[0],
      excerpt: "",
      tags: [],
      content: [""],
      images: [],
    });
    setFiles([]);
  };

  const handleDelete = async (id) => {
    if (
      await confirmDelete({
        title: "Delete Blog Post?",
        text: "This blog post will be permanently deleted.",
      })
    ) {
      try {
        await deleteBlog(id);
        successToast("Blog post deleted successfully.");
      } catch (err) {
        console.error(err);
        errorToast("Error deleting blog post.");
      }
    }
  };

  const handleEdit = (blog) => {
    setFormData({
      id: blog.id,
      title: blog.title,
      author: blog.author,
      date: blog.date,
      excerpt: blog.excerpt,
      tags: blog.tags,
      content: blog.content,
      images: blog.images || [],
    });
    setFiles([]);
  };

  return (
    <div className="p-6 space-y-8">
      <h2 className="text-3xl font-bold text-gray-800">Manage Blogs</h2>

      <div className="bg-white shadow-md rounded-lg p-6 space-y-4">
        <h3 className="text-xl font-semibold text-gray-700">
          {formData.id ? "Edit Blog" : "Post a New Blog"}
        </h3>

        <div>
          <label className="block font-semibold text-sm mb-1">Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className="block w-full p-2 border rounded"
            placeholder="Blog title"
          />
        </div>

        <div>
          <label className="block font-semibold text-sm mb-1">Author</label>
          <input
            type="text"
            name="author"
            value={formData.author}
            onChange={handleInputChange}
            className="block w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block font-semibold text-sm mb-1">Date</label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleInputChange}
            className="block w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block font-semibold text-sm mb-1">Excerpt</label>
          <textarea
            name="excerpt"
            value={formData.excerpt}
            onChange={handleInputChange}
            rows={3}
            className="block w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block font-semibold text-sm mb-1">
            Tags (comma-separated)
          </label>
          <input
            type="text"
            name="tags"
            value={formData.tags.join(", ")}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                tags: e.target.value.split(",").map((tag) => tag.trim()),
              }))
            }
            className="block w-full p-2 border rounded"
          />
        </div>

        <div>
          <label className="block font-semibold text-sm mb-1">Content</label>
          {formData.content.map((section, index) => (
            <div key={index} className="mb-4">
              <MDEditor
                value={section}
                onChange={(value) => handleContentChange(index, value || "")}
              />
              {formData.content.length > 1 && (
                <button
                  onClick={() => handleRemoveContentSection(index)}
                  className="text-red-500 text-sm mt-1"
                >
                  Remove Section
                </button>
              )}
            </div>
          ))}
          <button
            onClick={handleAddContentSection}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm"
          >
            Add Content Section
          </button>
        </div>

        <MultiImageUploadField
          label="Blog Images"
          hint="Recommended size: 800×600px"
          existingUrls={formData.images}
          newFiles={files}
          onAdd={(picked) => setFiles((prev) => [...prev, ...picked])}
          onRemove={(idx, isExisting) => {
            if (isExisting) {
              setFormData((prev) => ({
                ...prev,
                images: prev.images.filter((_, i) => i !== idx),
              }));
            } else {
              setFiles((prev) => prev.filter((_, i) => i !== idx));
            }
          }}
        />

        <div className="flex space-x-4 mt-4">
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-[#14251F] hover:bg-[#1f3a30] text-[#FFD1A0] rounded font-medium"
          >
            {formData.id ? "Update Blog" : "Post Blog"}
          </button>
          {formData.id && (
            <button
              onClick={resetForm}
              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Existing Blogs</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogs.map((blog) => (
            <div
              key={blog.id}
              className="bg-white shadow-md rounded-lg p-4 hover:shadow-lg transition-shadow flex flex-col"
            >
              <img
                src={blog.images?.[0] || "https://via.placeholder.com/150"}
                alt={blog.title}
                className="w-full h-40 object-cover rounded mb-4"
              />
              <h4 className="text-lg font-bold text-gray-800">{blog.title}</h4>
              <p className="text-sm text-gray-600 mt-1">{blog.excerpt}</p>
              <p className="text-xs text-gray-400 mt-2 mb-4">{blog.date}</p>
              <div className="flex flex-col gap-2 mt-auto">
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      handleEdit(blog);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="flex-1 bg-[#14251F] text-white text-sm font-medium py-1.5 rounded hover:bg-[#1e3a2e] transition-colors cursor-pointer"
                  >
                    ✏️ Edit
                  </button>
                  <a
                    href={`https://resort.thevivaak.com/blogs/${blog.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-center border border-[#14251F] text-[#14251F] text-sm font-medium py-1.5 rounded hover:bg-[#14251F]/5 transition-colors"
                  >
                    🔗 View
                  </a>
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(blog.id)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-1.5 rounded transition-colors cursor-pointer"
                >
                  🗑️ Delete Blog
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
